import AddRounded from '@mui/icons-material/AddRounded';
import HistoryEduRounded from '@mui/icons-material/HistoryEduRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { StatusChip } from '../../components/StatusChip';
import { EmptyState } from '../../components/states';
import { businessDate, dateTime, money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  applyApiErrors,
  contactKeys,
  defaultDirection,
  directionOptions,
  idempotency,
  openingBalanceEffect,
  openingBalanceSchema,
  todayIn,
  type Contact,
  type ContactKind,
  type Ledger,
  type OpeningBalance,
  type OpeningBalanceForm,
} from './model';

const PERM: Record<Ledger, string> = {
  RECEIVABLE: 'customers.opening_balance',
  PAYABLE: 'suppliers.opening_balance',
};

/** Opening balances brought over from before the system: list, record, reverse. */
export function OpeningBalancesTab({ contact, kind }: { contact: Contact; kind: ContactKind }) {
  const { can } = useAuth();
  const { format } = useShop();
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<OpeningBalance | null>(null);
  const list = useQuery({
    queryKey: contactKeys.openingBalances(contact.id),
    queryFn: () =>
      unwrap(api.GET('/contacts/{id}/opening-balances', { params: { path: { id: contact.id } } })),
  });
  const ledgers: Ledger[] = [
    ...(contact.isCustomer ? (['RECEIVABLE'] as const) : []),
    ...(contact.isSupplier ? (['PAYABLE'] as const) : []),
  ];
  const canAdd = ledgers.some((l) => can(PERM[l]));

  const columns: Column<OpeningBalance>[] = [
    {
      key: 'date',
      header: 'Effective date',
      render: (o) => (
        <Tooltip title={`Recorded ${dateTime(o.createdAt, format)}`}>
          <span>{businessDate(o.effectiveDate, format)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'ledger',
      header: 'Ledger',
      hideBelow: 'sm',
      render: (o) => (o.ledger === 'RECEIVABLE' ? 'Receivable' : 'Payable'),
    },
    {
      key: 'source',
      header: 'Source',
      hideBelow: 'md',
      render: (o) => (
        <Typography variant="body2" sx={{ maxWidth: 360 }} noWrap title={o.source}>
          {o.source}
        </Typography>
      ),
    },
    {
      key: 'direction',
      header: 'Meaning',
      render: (o) =>
        o.direction === 'OWED_TO_SHOP'
          ? `${contact.name} owes the shop`
          : `Shop owes ${contact.name}`,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (o) => (
        <Box
          sx={{
            textDecoration: o.reversed ? 'line-through' : undefined,
            opacity: o.reversed ? 0.6 : 1,
          }}
        >
          <Money value={o.amount} strong />
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (o) => <StatusChip status={o.reversed ? 'REVERSED' : 'POSTED'} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      render: (o) =>
        !o.reversed && can(PERM[o.ledger]) ? (
          <Tooltip title="Reverse (correction)">
            <Button
              size="small"
              color="inherit"
              sx={{ minWidth: 0, px: 1 }}
              aria-label="Reverse opening balance"
              onClick={() => setReversing(o)}
            >
              <UndoRounded fontSize="small" />
            </Button>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
          Balances carried over from before you started using the system. They appear as the first
          line of the ledger and can be settled by payments like an invoice.
        </Typography>
        {canAdd && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setAdding(true)}
            sx={{ flexShrink: 0 }}
          >
            Record opening balance
          </Button>
        )}
      </Stack>
      <DataTable
        columns={columns}
        rows={list.data?.data}
        rowKey={(o) => o.id}
        loading={list.isLoading}
        empty={
          <EmptyState
            icon={<HistoryEduRounded />}
            title="No opening balance"
            body={`If ${contact.name} had a balance in your old books, record it here so the ledger starts from the right figure.`}
            action={
              canAdd
                ? { label: 'Record opening balance', onClick: () => setAdding(true) }
                : undefined
            }
          />
        }
      />
      <OpeningBalanceDialog
        open={adding}
        contact={contact}
        kind={kind}
        ledgers={ledgers.filter((l) => can(PERM[l]))}
        onClose={() => setAdding(false)}
      />
      <ReverseDialog
        contact={contact}
        item={reversing}
        onClose={() => setReversing(null)}
        amountLabel={reversing ? money(reversing.amount, format) : ''}
      />
    </>
  );
}

export function OpeningBalanceDialog({
  open,
  contact,
  kind,
  ledgers,
  onClose,
}: {
  open: boolean;
  contact: Contact;
  kind: ContactKind;
  ledgers: Ledger[];
  onClose: () => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { format } = useShop();
  const notify = useNotify();
  const qc = useQueryClient();
  const today = todayIn(format.timeZone);
  const first: Ledger =
    ledgers.find((l) => l === (kind === 'customer' ? 'RECEIVABLE' : 'PAYABLE')) ??
    ledgers[0] ??
    'RECEIVABLE';
  const defaults = (): OpeningBalanceForm => ({
    ledger: first,
    direction: defaultDirection(first),
    amount: '',
    effectiveDate: today,
    source: '',
  });
  const { register, handleSubmit, control, reset, setValue, setError, formState } =
    useForm<OpeningBalanceForm>({
      resolver: zodResolver(openingBalanceSchema(today)),
      defaultValues: defaults(),
      mode: 'onBlur',
    });
  const { errors } = formState;
  const [confirming, setConfirming] = useState<OpeningBalanceForm | null>(null);
  // Reset when the dialog opens: the confirmation step via derived state, the form in an effect.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setConfirming(null);
  }
  useEffect(() => {
    if (open) reset(defaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the dialog opens
  }, [open]);
  const ledger = useWatch({ control, name: 'ledger' });

  const save = useMutation({
    mutationFn: (v: OpeningBalanceForm) =>
      unwrap(
        api.POST('/contacts/{id}/opening-balances', {
          params: { path: { id: contact.id }, header: idempotency() },
          body: { ...v, amount: v.amount.trim(), source: v.source.trim() },
        }),
      ),
    onSuccess: async () => {
      notify.success('Opening balance recorded');
      await qc.invalidateQueries({ queryKey: contactKeys.all });
      onClose();
    },
    onError: (e) => {
      setConfirming(null);
      if (
        !applyApiErrors(
          e,
          ['ledger', 'direction', 'amount', 'effectiveDate', 'source'] as const,
          setError,
        )
      )
        notify.error(e);
    },
  });

  const party = kind === 'customer' ? 'customer' : 'supplier';
  return (
    <Dialog
      open={open}
      onClose={save.isPending ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        noValidate
        onSubmit={(e) => void handleSubmit((v) => setConfirming(v))(e)}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Record opening balance</DialogTitle>
        <DialogContent>
          {confirming ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Box
                sx={(t) => {
                  const p = (t.vars ?? t).palette.primary.main;
                  return {
                    p: 2.5,
                    borderRadius: '14px',
                    textAlign: 'center',
                    bgcolor: `color-mix(in srgb, ${p} 6%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${p} 16%, transparent)`,
                  };
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  {confirming.ledger === 'RECEIVABLE' ? 'Receivable' : 'Payable'} ·{' '}
                  {businessDate(confirming.effectiveDate, format)}
                </Typography>
                <Typography variant="h3" className="tabular">
                  {money(confirming.amount, format)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {confirming.direction === 'OWED_TO_SHOP'
                    ? `${contact.name} owes the shop`
                    : `The shop owes ${contact.name}`}
                </Typography>
              </Box>
              <Alert severity="info" variant="outlined">
                {openingBalanceEffect(confirming, contact.name, money(confirming.amount, format))} A
                posted opening balance can only be corrected by reversing it.
              </Alert>
            </Stack>
          ) : (
            <Stack spacing={2.25} sx={{ pt: 0.5 }}>
              {ledgers.length > 1 && (
                <Controller
                  control={control}
                  name="ledger"
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={field.value}
                      onChange={(_, v: Ledger | null) => {
                        if (!v) return;
                        field.onChange(v);
                        setValue('direction', defaultDirection(v));
                      }}
                    >
                      <ToggleButton value="RECEIVABLE">As customer (receivable)</ToggleButton>
                      <ToggleButton value="PAYABLE">As supplier (payable)</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              )}
              <Controller
                control={control}
                name="direction"
                render={({ field }) => (
                  <RadioGroup value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                    {directionOptions(ledger, contact.name).map((o) => (
                      <FormControlLabel
                        key={o.value}
                        value={o.value}
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {o.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {o.hint}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          alignItems: 'flex-start',
                          mb: 0.5,
                          '& .MuiRadio-root': { mt: -0.25 },
                        }}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Amount"
                  required
                  inputMode="decimal"
                  autoFocus
                  {...register('amount')}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">{format.currencyCode}</InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label="As of date"
                  type="date"
                  required
                  {...register('effectiveDate')}
                  error={!!errors.effectiveDate}
                  helperText={
                    errors.effectiveDate?.message ?? 'Usually the day before you went live'
                  }
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
                />
              </Stack>
              <TextField
                label="Source"
                required
                placeholder={`e.g. Balance from paper khata for this ${party}`}
                {...register('source')}
                error={!!errors.source}
                helperText={
                  errors.source?.message ?? 'Where this figure comes from, for your records'
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {confirming ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setConfirming(null)}
                disabled={save.isPending}
              >
                Back
              </Button>
              <Button
                variant="contained"
                loading={save.isPending}
                onClick={() => save.mutate(confirming)}
              >
                Post opening balance
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Review
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function ReverseDialog({
  contact,
  item,
  amountLabel,
  onClose,
}: {
  contact: Contact;
  item: OpeningBalance | null;
  amountLabel: string;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  // Start fresh whenever a different opening balance is picked (derived-state reset, no effect).
  const [shownItem, setShownItem] = useState(item);
  if (item !== shownItem) {
    setShownItem(item);
    setReason('');
    setTouched(false);
  }
  const reverse = useMutation({
    mutationFn: (o: OpeningBalance) =>
      unwrap(
        api.POST('/contacts/{id}/opening-balances/{openingBalanceId}/reverse', {
          params: { path: { id: contact.id, openingBalanceId: o.id }, header: idempotency() },
          body: { reason: reason.trim() },
        }),
      ),
    onSuccess: async () => {
      notify.success('Opening balance reversed');
      await qc.invalidateQueries({ queryKey: contactKeys.all });
      onClose();
    },
    onError: (e) => notify.error(e),
  });
  const invalid = reason.trim().length < 3;
  return (
    <Dialog open={!!item} onClose={reverse.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Reverse opening balance?</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          This posts a mirror entry dated today that cancels the {amountLabel} opening balance of{' '}
          {contact.name}. It cannot be undone; record a new opening balance if the figure was wrong.
        </Typography>
        <TextField
          label="Reason"
          required
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && invalid}
          helperText={touched && invalid ? 'At least 3 characters' : 'Kept in the audit trail'}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={reverse.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          loading={reverse.isPending}
          disabled={invalid}
          onClick={() => item && reverse.mutate(item)}
        >
          Reverse
        </Button>
      </DialogActions>
    </Dialog>
  );
}
